import React, { useEffect, useMemo, useRef, useState } from "react";
import SpeakerDistributionSvg from "./speaker-distribution.svg";

import styles from "./SpeakerDistributionDiagram.module.scss";

export type SpeakerDistributionItem = "label-lfe" | "label-main-system" | "label-front-fills" | "label-celling-system";

interface SpeakerDistributionDiagramProps {
  onItemSelect: (item: SpeakerDistributionItem) => void;
  activeItems: SpeakerDistributionItem[];
}

export const SpeakerDistributionDiagram: React.FC<SpeakerDistributionDiagramProps> = ({
  onItemSelect,
  activeItems,
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState<boolean>(false);

  useEffect(() => {
    setMounted(true);
  }, [divRef]);

  // memoize item handles
  const itemHandles = useMemo<Partial<Record<SpeakerDistributionItem, Element>>>(() => {
    if (!mounted) {
      return {};
    }

    return {
      "label-lfe": divRef.current!.querySelector(".label-lfe") ?? undefined,
      "label-main-system": divRef.current!.querySelector(".label-main-system") ?? undefined,
      "label-front-fills": divRef.current!.querySelector(".label-front-fills") ?? undefined,
      "label-celling-system": divRef.current!.querySelector(".label-celling-system") ?? undefined,
    };
  }, [divRef, mounted]);

  // setup click listeners
  useEffect(() => {
    const listeners: Record<string, () => void> = {};

    Object.entries(itemHandles).forEach(([id, handle]) => {
      handle.classList.add("item");
      listeners[id] = () => onItemSelect(id as SpeakerDistributionItem);
      handle.addEventListener("click", listeners[id]);
    });

    return () => {
      Object.entries(itemHandles).forEach(([id, handle]) => {
        handle.removeEventListener("click", listeners[id]);
      });
    };
  }, [mounted, itemHandles, onItemSelect]);

  // add active class based on props
  useEffect(() => {
    Object.entries(itemHandles).forEach(([id, handle]) => {
      if (activeItems?.includes(id as SpeakerDistributionItem)) {
        handle.classList.add("active");
      } else {
        handle.classList.remove("active");
      }
    });
  }, [itemHandles, activeItems]);

  return (
    <div ref={divRef} className={styles.root}>
      <SpeakerDistributionSvg />
    </div>
  );
};
